import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';

import { THIN_SCROLLBAR } from '../../styles/scrollbar';

import {
  allLanguageDirectives,
  projectGatewayHistory,
  type ChatSession,
  type CustomFolder,
  type SessionFolder,
} from '@paradigm/terminal-core';

import { useAgentActions, useAgentState } from '../../providers/contexts';
import { useTabsActions, useTabsState } from '../../providers/TabsProvider';
import { useTerminalConfig } from '../../context/config-context';
import { buildTerminalStores } from '../../config';
import {
  ComposeIcon,
  ExamplesIcon,
  FolderIcon,
  FolderOpenIcon,
  NewChatIcon,
  PersonalizationIcon,
  PinIcon,
  SidebarIcon,
  SkillsIcon,
  TasksIcon,
} from '../icons';
import { useIsBelowMd } from './hooks';
import { DEFAULT_FOLDER_ID, DEFAULT_FOLDER_LABEL } from './constants';
import SessionRow, { SessionList } from './SessionRow';
import ConfirmDelete from './ConfirmDelete';
import SidebarAccountButton from './AccountButton';
import {
  FolderContent,
  FolderContentInner,
  FolderContextMenu,
  FolderEllipsisButton,
  FolderEmpty,
  FolderIconLayer,
  FolderIconSwap,
  FolderInlineInput,
  FolderInlineRename,
  FolderLabel,
  FolderNewChatButton,
  FolderRow,
  FolderRowWithLongPress,
  FolderSection,
  FolderSessionList,
  SessionDropZone,
} from './FolderRow';

interface RecentsViewProps {
  /**
   * Compact mode only: collapse the sidebar. When provided, a collapse control
   * renders at the top of the sidebar — in compact mode the package's top bar
   * (which normally holds the toggle) is hidden. Omit for the default layout.
   */
  readonly onToggleSidebar?: () => void;
}

export default function RecentsView({ onToggleSidebar }: RecentsViewProps) {
  const state = useAgentState();
  const actions = useAgentActions();
  const { setActiveTab } = useTabsActions();
  const { activeTab } = useTabsState();
  const config = useTerminalConfig();
  const { historyStore } = useMemo(
    () => buildTerminalStores(config),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config.namespace],
  );
  const accountId = config.accountId;
  // Default folder for sessions created by this host (was hardcoded
  // `'paradex'` in the source's `createSession({ folder })` calls).
  const defaultFolder = (config.defaultFolder ??
    config.namespace) as SessionFolder;
  const isBelowMd = useIsBelowMd();

  // host-owned: on mobile the host drawer collapsed the sidebar after a
  // selection (`drawerActions.setSidebarCollapsed(true)`). That drawer
  // state is owned by the host and has no package seam, so this is now a
  // no-op. FLAG: mobile sidebar-collapse-on-select behavior is dropped.
  // `isBelowMd` is referenced here so the value stays observed for any
  // future in-component mobile UX.
  const collapseSidebarOnMobile = useCallback(() => {
    void isBelowMd;
  }, [isBelowMd]);
  const [searchQuery, setSearchQuery] = useState('');
  // Drives the inline "+ New Folder" → name input flow. When non-null,
  // the input renders above the folder list seeded with this value.
  const [creatingFolderName, setCreatingFolderName] = useState<string | null>(
    null,
  );
  const [renamingFolderId, setRenamingFolderId] = useState<string | null>(null);
  const [confirmingDeleteFolderId, setConfirmingDeleteFolderId] = useState<
    string | null
  >(null);
  const [folderMenuId, setFolderMenuId] = useState<string | null>(null);
  // Stable so `FolderContextMenu`'s mousedown/keydown effect (deps `[onClose]`)
  // doesn't re-subscribe its window listeners on every parent render.
  const closeFolderMenu = useCallback(() => setFolderMenuId(null), []);
  const [folderMenuPos, setFolderMenuPos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  // Per-folder collapsed state. Folder ids that appear here with `true`
  // render closed; everything else (including folders the user hasn't
  // interacted with yet) defaults to open.
  const [collapsedFolders, setCollapsedFolders] = useState<
    Record<string, boolean>
  >({});
  const [deepMatches, setDeepMatches] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  // The most recent query that finished its deep-search pass. Derived
  // `isDeepSearching` flips synchronously by comparing this against the
  // live input — so the shimmer paints on the very first render after a
  // keystroke, without waiting for an effect.
  const [searchedQuery, setSearchedQuery] = useState('');
  /**
   * How many chats the last search could not look inside.
   *
   * Searching reads the copy of each conversation this device has cached,
   * and the only thing that writes one is opening the chat (or a reconnect
   * while it is open). So a chat never opened here has no text to search,
   * and reporting a flat "no chats match" for it was a wrong answer rather
   * than an incomplete one — the message the user was looking for may well
   * be in one of them. Counted so the result can say what it did not cover.
   */
  const [unsearchable, setUnsearchable] = useState(0);

  // Cached lower-cased searchable text per session. Built lazily on the
  // first deep-search trigger and only rebuilt when the session list
  // shape changes — so steady-state typing pays O(1) per keystroke.
  const indexRef = useRef<{
    key: string;
    index: Map<string, string> | null;
    unsearchable: number;
  }>({ key: '', index: null, unsearchable: 0 });

  // App context is the host namespace — chats are stored per-account, so
  // this list is intentionally flat: show every non-temporary session for
  // the current account, sorted pinned-first then most-recently-updated.
  const trimmedQuery = searchQuery.trim().toLowerCase();

  // Folder names are searchable too: if the query matches a folder
  // name, that folder counts as a hit (rendered with all its chats)
  // even when none of its chats match individually.
  const matchedFolderIds = useMemo(() => {
    const ids = new Set<string>();
    if (trimmedQuery.length === 0) return ids;
    for (const f of state.customFolders) {
      if (f.name.toLowerCase().includes(trimmedQuery)) ids.add(f.id);
    }
    return ids;
  }, [state.customFolders, trimmedQuery]);

  const titleMatches = useMemo(() => {
    return state.sessions.filter((s) => {
      if (s.isTemporary === true) return false;
      // A chat opened but never sent in. It has no name of its own and nothing
      // in it, so a row for it is a row the user cannot tell from any other
      // "New Chat" — including the one they are composing in right now.
      if (s.isDraft === true) return false;
      if (trimmedQuery.length === 0) return true;
      if (s.title.toLowerCase().includes(trimmedQuery)) return true;
      // A session also matches when its parent folder's name matched —
      // searching the folder name pulls the whole folder into view.
      if (s.customFolderId != null && matchedFolderIds.has(s.customFolderId)) {
        return true;
      }
      return false;
    });
  }, [state.sessions, trimmedQuery, matchedFolderIds]);

  // Deep-search fallback: only fires when the title filter has zero
  // hits and the query is non-empty. The index is built the first time
  // this branch runs in a session, then reused for every subsequent
  // zero-title-match search until the session list changes.
  //
  // Keyed on the chats AND on how many times a cached conversation has been
  // written. `updatedAt` alone is the roster's field: it does not move when a
  // read replaces the cached copy of a chat, so the index built the first
  // time the user searched went on answering from the text a chat held when
  // it was first opened — opening a chat, or receiving a turn in it, never
  // refreshed what was searchable inside it.
  const sessionsKey = useMemo(
    () =>
      [
        historyStore.transcriptCacheVersion(),
        ...state.sessions.map((s) => `${s.sessionId}:${s.updatedAt}`),
      ].join('|'),
    // `state.messages` is not read here, but a write to the cache is what
    // makes the version move, and a rendered message is the closest signal
    // this component has that one may have happened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.sessions, state.messages, historyStore],
  );

  useEffect(() => {
    if (trimmedQuery.length === 0 || titleMatches.length > 0) {
      setDeepMatches(new Set());
      setSearchedQuery(trimmedQuery);
      return;
    }
    if (accountId == null) {
      setSearchedQuery(trimmedQuery);
      return;
    }

    // 250ms keeps the shimmer visible long enough to register as a
    // transition (a setTimeout(0) finishes in one frame and looks
    // like nothing happened), and naturally debounces typing.
    const handle = window.setTimeout(() => {
      if (
        indexRef.current.index == null ||
        indexRef.current.key !== sessionsKey
      ) {
        const index = new Map<string, string>();
        let missing = 0;
        for (const s of state.sessions) {
          if (s.isTemporary === true || s.isDraft === true) continue;
          // Searching a chat searches the AGENT's own record of it, cached
          // on this device by the last read and projected the same way the
          // chat view projects it. Projected rather than read raw so the
          // framing the app prepends to every outbound message — the host
          // preamble, the language directive — is stripped first: searching
          // the raw rows matched that boilerplate and so returned every chat
          // for a word the user never typed.
          const messages =
            accountId != null
              ? projectGatewayHistory(
                  historyStore.loadSessionTranscript(accountId, s.sessionId),
                  {
                    systemPreamble: config.systemPreamble,
                    languageDirectives: allLanguageDirectives(),
                  },
                )
              : [];
          const text = messages
            .map((m) => m.content)
            .join('\n')
            .toLowerCase();
          // Nothing cached means nothing to search, which is not the same as
          // searching it and finding nothing. Counted rather than indexed as
          // an empty string, so the result can admit what it missed.
          if (text.trim().length === 0) {
            missing += 1;
            continue;
          }
          index.set(s.sessionId, text);
        }
        indexRef.current = { key: sessionsKey, index, unsearchable: missing };
      }
      const matches = new Set<string>();
      const built = indexRef.current.index;
      if (built == null) return;
      built.forEach((text: string, sessionId: string) => {
        if (text.includes(trimmedQuery)) matches.add(sessionId);
      });
      setDeepMatches(matches);
      setUnsearchable(indexRef.current.unsearchable);
      setSearchedQuery(trimmedQuery);
    }, 250);

    return () => window.clearTimeout(handle);
  }, [
    trimmedQuery,
    titleMatches.length,
    accountId,
    sessionsKey,
    state.sessions,
    historyStore,
    config.systemPreamble,
  ]);

  // Derived synchronously so it's already true on the first render
  // after a keystroke — no effect-tick delay before the shimmer paints.
  const isDeepSearching =
    trimmedQuery.length > 0 &&
    titleMatches.length === 0 &&
    searchedQuery !== trimmedQuery;

  const displaySessions = useMemo(() => {
    let visible: readonly ChatSession[];
    if (trimmedQuery.length === 0 || titleMatches.length > 0) {
      visible = titleMatches;
    } else if (searchedQuery === trimmedQuery && deepMatches.size > 0) {
      visible = state.sessions.filter(
        (s) =>
          s.isTemporary !== true &&
          s.isDraft !== true &&
          deepMatches.has(s.sessionId),
      );
    } else {
      visible = [];
    }
    return [...visible].sort((a, b) => {
      if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
      return b.updatedAt - a.updatedAt;
    });
  }, [state.sessions, trimmedQuery, titleMatches, deepMatches, searchedQuery]);

  const handleNewChat = useCallback(() => {
    // If the active session is already empty (no messages exchanged
    // yet), just focus that one instead of creating another blank
    // chat — saves the user from accumulating zombie "New Chat" rows.
    const active = state.sessions.find(
      (s) => s.sessionId === state.activeSessionId,
    );
    const activeIsEmpty =
      active != null &&
      active.isTemporary !== true &&
      state.messages.length === 0;
    if (activeIsEmpty) {
      setActiveTab('chat');
      collapseSidebarOnMobile();
      return;
    }
    actions.createSession({ folder: defaultFolder });
    setActiveTab('chat');
    collapseSidebarOnMobile();
  }, [
    actions,
    setActiveTab,
    state.activeSessionId,
    state.messages.length,
    state.sessions,
    collapseSidebarOnMobile,
    defaultFolder,
  ]);

  // Create a chat directly inside a custom folder (the compose button on
  // the folder row). Expands the folder so the new chat is visible.
  const handleNewChatInFolder = useCallback(
    (folderId: string) => {
      const sessionId = actions.createSession({ folder: defaultFolder });
      actions.setSessionFolder(sessionId, folderId);
      setCollapsedFolders((prev) => ({ ...prev, [folderId]: false }));
      setActiveTab('chat');
      collapseSidebarOnMobile();
    },
    [actions, defaultFolder, setActiveTab, collapseSidebarOnMobile],
  );

  const handleSelectSession = useCallback(
    (sessionId: string) => {
      if (sessionId === state.activeSessionId) {
        setActiveTab('chat');
        collapseSidebarOnMobile();
        return;
      }
      actions.switchSession(sessionId);
      setActiveTab('chat');
      collapseSidebarOnMobile();
    },
    [actions, setActiveTab, state.activeSessionId, collapseSidebarOnMobile],
  );

  // Group sessions by their effective folder: a custom folder if
  // `customFolderId` matches one of the user's folders, otherwise the
  // default folder. (Sessions referencing a deleted folder fall back to
  // default — the agent-context `deleteCustomFolder` already strips the
  // field, but defending here keeps the UI sane if persisted state
  // drifts.)
  const customFolderIdSet = useMemo(
    () => new Set(state.customFolders.map((f) => f.id)),
    [state.customFolders],
  );

  const sessionsByFolder = useMemo(() => {
    const groups = new Map<string, ChatSession[]>();
    groups.set(DEFAULT_FOLDER_ID, []);
    for (const f of state.customFolders) groups.set(f.id, []);
    for (const s of displaySessions) {
      const key =
        s.customFolderId != null && customFolderIdSet.has(s.customFolderId)
          ? s.customFolderId
          : DEFAULT_FOLDER_ID;
      groups.get(key)!.push(s);
    }
    return groups;
  }, [displaySessions, state.customFolders, customFolderIdSet]);

  // Folder ordering: pinned first, then most-recently-created on top
  // (newest createdAt wins). Splitting pinned vs unpinned so the
  // inline "+ New Folder" input can slot in between them — pinned
  // items stay above; unpinned folders + chats sit below.
  const sortedCustomFolders = useMemo(
    () =>
      [...state.customFolders].sort((a, b) => {
        const aPinned = a.pinned === true;
        const bPinned = b.pinned === true;
        if (aPinned !== bPinned) return aPinned ? -1 : 1;
        return b.createdAt - a.createdAt;
      }),
    [state.customFolders],
  );
  // While searching, hide folders that have neither a name match nor
  // any matching session inside. Empty/unmatched folders during a
  // search would just be noise.
  const isSearchActive = trimmedQuery.length > 0;
  const visibleFolders = useMemo(() => {
    if (!isSearchActive) return sortedCustomFolders;
    return sortedCustomFolders.filter(
      (f) =>
        matchedFolderIds.has(f.id) ||
        (sessionsByFolder.get(f.id)?.length ?? 0) > 0,
    );
  }, [isSearchActive, sortedCustomFolders, matchedFolderIds, sessionsByFolder]);
  const pinnedFolders = useMemo(
    () => visibleFolders.filter((f) => f.pinned === true),
    [visibleFolders],
  );
  const unpinnedFolders = useMemo(
    () => visibleFolders.filter((f) => f.pinned !== true),
    [visibleFolders],
  );

  // Unfiled sessions render in the "Sessions" area below all folders.
  // Already pinned-first thanks to the `displaySessions` sort, so we
  // don't need to split them — pinning a chat just floats it to the
  // top of this list, never above the folder area.
  const unfiledSessions = sessionsByFolder.get(DEFAULT_FOLDER_ID) ?? [];

  const handleStartCreatingFolder = useCallback(() => {
    setCreatingFolderName('');
  }, []);

  const handleCommitNewFolder = useCallback(() => {
    if (creatingFolderName == null) return;
    const trimmed = creatingFolderName.trim();
    if (trimmed.length > 0) {
      const folderId = actions.createCustomFolder(trimmed);
      // New folders start collapsed — it's empty at creation, so there's
      // nothing worth showing open by default.
      setCollapsedFolders((prev) => ({ ...prev, [folderId]: true }));
    }
    setCreatingFolderName(null);
  }, [actions, creatingFolderName]);

  const handleCancelNewFolder = useCallback(() => {
    setCreatingFolderName(null);
  }, []);

  const handleStartRenameFolder = useCallback((folderId: string) => {
    setRenamingFolderId(folderId);
  }, []);

  const handleCommitRenameFolder = useCallback(
    (folderId: string, name: string) => {
      const trimmed = name.trim();
      if (trimmed.length > 0) {
        actions.renameCustomFolder(folderId, trimmed);
      }
      setRenamingFolderId(null);
    },
    [actions],
  );

  // List of folders shown in the session context-menu's "Move to" view.
  // Includes the default folder so users can move a chat back out of a
  // custom folder.
  const moveTargets = useMemo(
    () => [
      { id: DEFAULT_FOLDER_ID, name: DEFAULT_FOLDER_LABEL },
      ...state.customFolders.map((f) => ({ id: f.id, name: f.name })),
    ],
    [state.customFolders],
  );

  const handleMoveSession = useCallback(
    (sessionId: string, folderId: string) => {
      // Skip the round-trip if the chat is already in the target folder.
      // Dropping a session on the folder it lives in (very easy with the
      // whole FolderSection acting as the drop zone) would otherwise queue
      // a redundant `setSessionFolder` mutation.
      const session = state.sessions.find((s) => s.sessionId === sessionId);
      const currentFolderId = session?.customFolderId ?? DEFAULT_FOLDER_ID;
      if (currentFolderId === folderId) return;
      actions.setSessionFolder(
        sessionId,
        folderId === DEFAULT_FOLDER_ID ? undefined : folderId,
      );
    },
    [actions, state.sessions],
  );

  const toggleFolderCollapsed = useCallback((folderId: string) => {
    setCollapsedFolders((prev) => ({
      ...prev,
      // Treat unset (`undefined`) as expanded → next state is collapsed.
      [folderId]: prev[folderId] !== true,
    }));
  }, []);

  // Folders auto-expand while the user is searching so matches in any
  // folder are visible without an extra click.
  const isSearching = trimmedQuery.length > 0;
  const isFolderOpen = useCallback(
    (folderId: string) => isSearching || collapsedFolders[folderId] !== true,
    [isSearching, collapsedFolders],
  );

  // The "nothing to show" branch must also account for folder-name
  // matches: searching for an empty folder by name should still
  // surface the folder row instead of the empty-state hint.
  const hasNoVisibleContent =
    displaySessions.length === 0 &&
    visibleFolders.length === 0 &&
    creatingFolderName == null;

  // Render a custom-folder section. Hoisted into a closure so the
  // pinned and unpinned passes don't duplicate ~80 lines of JSX.
  const renderFolderSection = (folder: CustomFolder) => {
    const folderSessions = sessionsByFolder.get(folder.id) ?? [];
    const isRenaming = renamingFolderId === folder.id;
    const open = isFolderOpen(folder.id);
    const isConfirmingDelete = confirmingDeleteFolderId === folder.id;
    const isMenuOpen = folderMenuId === folder.id;
    return (
      <SessionDropZone
        key={folder.id}
        folderId={folder.id}
        onDropSession={handleMoveSession}
      >
        <FolderSection>
          <FolderRowWithLongPress
            $open={open}
            onClick={() => {
              if (isRenaming) return;
              toggleFolderCollapsed(folder.id);
            }}
            onLongPress={({ x, y }) => {
              if (isRenaming) return;
              setFolderMenuId(folder.id);
              setFolderMenuPos({ x, y });
            }}
          >
            {folder.pinned === true && <PinIcon />}
            <FolderIconSwap>
              <FolderIconLayer $visible={!open}>
                <FolderIcon />
              </FolderIconLayer>
              <FolderIconLayer $visible={open}>
                <FolderOpenIcon />
              </FolderIconLayer>
            </FolderIconSwap>
            {isRenaming ? (
              <FolderInlineRename
                initialValue={folder.name}
                onCommit={(name) => handleCommitRenameFolder(folder.id, name)}
                onCancel={() => setRenamingFolderId(null)}
              />
            ) : (
              <FolderLabel>{folder.name}</FolderLabel>
            )}
            <FolderNewChatButton
              type="button"
              aria-label={`New chat in ${folder.name}`}
              title="New chat in this folder"
              onClick={(e) => {
                e.stopPropagation();
                handleNewChatInFolder(folder.id);
              }}
            >
              <ComposeIcon />
            </FolderNewChatButton>
            {isConfirmingDelete ? (
              <ConfirmDelete
                onConfirm={(e) => {
                  e.stopPropagation();
                  actions.deleteCustomFolder(folder.id);
                  setConfirmingDeleteFolderId(null);
                }}
                onCancel={(e) => {
                  e.stopPropagation();
                  setConfirmingDeleteFolderId(null);
                }}
              />
            ) : (
              <FolderEllipsisButton
                type="button"
                aria-label="Folder actions"
                data-menu-toggle
                onClick={(e) => {
                  e.stopPropagation();
                  // Second click on the dots closes the open menu (toggle).
                  if (isMenuOpen) {
                    setFolderMenuId(null);
                    return;
                  }
                  const rect = (
                    e.currentTarget as HTMLElement
                  ).getBoundingClientRect();
                  setFolderMenuId(folder.id);
                  setFolderMenuPos({
                    x: rect.right - 140,
                    y: rect.bottom,
                  });
                }}
              >
                ⋮
              </FolderEllipsisButton>
            )}
          </FolderRowWithLongPress>
          {isMenuOpen && folderMenuPos != null ? (
            <FolderContextMenu
              x={folderMenuPos.x}
              y={folderMenuPos.y}
              pinned={folder.pinned === true}
              onClose={closeFolderMenu}
              onPin={() => {
                actions.pinCustomFolder(folder.id, !(folder.pinned === true));
                setFolderMenuId(null);
              }}
              onRename={() => {
                handleStartRenameFolder(folder.id);
                setFolderMenuId(null);
              }}
              onDelete={() => {
                setConfirmingDeleteFolderId(folder.id);
                setFolderMenuId(null);
              }}
            />
          ) : null}
          <FolderContent $open={open}>
            <FolderContentInner>
              {folderSessions.length > 0 ? (
                <FolderSessionList>
                  {folderSessions.map((s) => (
                    <SessionRow
                      key={s.sessionId}
                      session={s}
                      active={s.sessionId === state.activeSessionId}
                      searchQuery={trimmedQuery}
                      moveTargets={moveTargets}
                      onSelect={handleSelectSession}
                      onRename={actions.renameSession}
                      onPin={actions.pinSession}
                      onDelete={actions.deleteSession}
                      onMove={handleMoveSession}
                    />
                  ))}
                </FolderSessionList>
              ) : (
                <FolderEmpty>No chats yet</FolderEmpty>
              )}
            </FolderContentInner>
          </FolderContent>
        </FolderSection>
      </SessionDropZone>
    );
  };

  return (
    <Container>
      <Header>
        {onToggleSidebar != null && (
          <CompactHeaderRow>
            <CollapseSidebarButton
              type="button"
              aria-label="Collapse sidebar"
              title="Collapse sidebar"
              onClick={onToggleSidebar}
            >
              <SidebarIcon />
            </CollapseSidebarButton>
          </CompactHeaderRow>
        )}
        <PrimaryButton type="button" onClick={handleNewChat}>
          <ButtonIconSlot>
            <NewChatIcon />
          </ButtonIconSlot>
          New Chat
        </PrimaryButton>
        <PrimaryButton type="button" onClick={handleStartCreatingFolder}>
          <ButtonIconSlot>
            <FolderIcon />
          </ButtonIconSlot>
          New Folder
        </PrimaryButton>
        <PrimaryButton
          type="button"
          $active={activeTab === 'skills'}
          onClick={() => {
            setActiveTab('skills');
            collapseSidebarOnMobile();
          }}
        >
          <ButtonIconSlot>
            <SkillsIcon />
          </ButtonIconSlot>
          Apps &amp; Skills
        </PrimaryButton>
        <PrimaryButton
          type="button"
          $active={activeTab === 'examples'}
          onClick={() => {
            setActiveTab('examples');
            collapseSidebarOnMobile();
          }}
        >
          <ButtonIconSlot>
            <ExamplesIcon />
          </ButtonIconSlot>
          Examples
        </PrimaryButton>
        {config.features?.cron === true && (
          <PrimaryButton
            type="button"
            $active={activeTab === 'cron'}
            onClick={() => {
              setActiveTab('cron');
              collapseSidebarOnMobile();
            }}
          >
            <ButtonIconSlot>
              <TasksIcon />
            </ButtonIconSlot>
            Tasks
          </PrimaryButton>
        )}
        {/* Ungated: the pane owns its own "unavailable" state. */}
        <PrimaryButton
          type="button"
          $active={activeTab === 'personalization'}
          onClick={() => {
            setActiveTab('personalization');
            collapseSidebarOnMobile();
          }}
        >
          <ButtonIconSlot>
            {/* Muted here, unlike in the settings nav where the icon lights up
                with its active row — the sidebar's own label is the bright
                thing, so the glyph beside it stays secondary. */}
            <PersonalizationIcon color="var(--text-secondary)" />
          </ButtonIconSlot>
          Personalization
        </PrimaryButton>
      </Header>
      <SearchBar>
        <SearchInput
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search chats and folders"
          aria-label="Search chats and folders"
        />
      </SearchBar>
      <ScrollArea>
        {hasNoVisibleContent ? (
          <EmptyHint>
            {isDeepSearching ? (
              <SearchingShimmer>Searching chat contents…</SearchingShimmer>
            ) : searchQuery.trim().length > 0 ? (
              <>
                No chats match &ldquo;{searchQuery.trim()}&rdquo;.
                {/* Searching looks inside the copy of each conversation this
                    device has, and only opening a chat creates one. Saying
                    "no matches" flat while some chats could not be looked
                    inside at all is a wrong answer, not an incomplete one:
                    what the user is looking for may be in one of them. */}
                {unsearchable > 0 && (
                  <UnsearchableNote>
                    {unsearchable === 1
                      ? '1 chat hasn’t been opened on this device, so its messages weren’t searched. Open it to include it.'
                      : `${unsearchable} chats haven’t been opened on this device, so their messages weren’t searched. Open them to include them.`}
                  </UnsearchableNote>
                )}
              </>
            ) : state.rosterStatus === 'loading' ? (
              // The chat list comes from the agent, and the cached copy this
              // painted from can be empty on a device that has never read it
              // — a new browser, or one whose storage was cleared. Until the
              // read lands, "no chats yet" is a claim about the CACHE that
              // reads as "your history is gone".
              <SearchingShimmer>Loading chats…</SearchingShimmer>
            ) : state.rosterStatus === 'unavailable' ? (
              <>Couldn&rsquo;t load your chats. Reconnecting…</>
            ) : (
              <>
                No chats yet — click <em>+ New Chat</em> to start one.
              </>
            )}
          </EmptyHint>
        ) : (
          <>
            {/* Pinned folders, with the pin icon next to the folder. */}
            {pinnedFolders.map(renderFolderSection)}
            {/* Inline "+ New Folder" input lands just below pinned
                folders so newly-created folders surface above the rest. */}
            {creatingFolderName != null && (
              <FolderRow>
                <FolderIcon />
                <FolderInlineInput
                  // The input is created on demand when the user clicks
                  // "+ New Folder"; auto-focusing follows the same
                  // pattern as the session rename input below.
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                  value={creatingFolderName}
                  placeholder="Folder name"
                  onChange={(e) => setCreatingFolderName(e.target.value)}
                  onBlur={handleCommitNewFolder}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleCommitNewFolder();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      handleCancelNewFolder();
                    }
                  }}
                />
              </FolderRow>
            )}
            {/* Unpinned folders next, newest first. */}
            {unpinnedFolders.map(renderFolderSection)}
            {/* Divider above the unfiled sessions zone. Shown whenever folders
                exist, even if the zone is empty — without the divider the
                drop-target area below looks like accidental whitespace. */}
            {(pinnedFolders.length > 0 || unpinnedFolders.length > 0) && (
              <SectionDivider />
            )}
            {/* Unfiled "Sessions" — always rendered when at least one folder
                exists, so a chat sitting inside a folder always has somewhere
                to be dragged back to. Empty state is its own drop target with
                a hint so the affordance is obvious during a drag. Dropping
                here moves the session out of any folder (folderId ===
                DEFAULT_FOLDER_ID ⇒ undefined in handleMoveSession). */}
            {(unfiledSessions.length > 0 ||
              pinnedFolders.length > 0 ||
              unpinnedFolders.length > 0) && (
              <SessionDropZone
                folderId={DEFAULT_FOLDER_ID}
                onDropSession={handleMoveSession}
              >
                {unfiledSessions.length > 0 ? (
                  <SessionList>
                    {unfiledSessions.map((s) => (
                      <SessionRow
                        key={s.sessionId}
                        session={s}
                        active={s.sessionId === state.activeSessionId}
                        searchQuery={trimmedQuery}
                        moveTargets={moveTargets}
                        onSelect={handleSelectSession}
                        onRename={actions.renameSession}
                        onPin={actions.pinSession}
                        onDelete={actions.deleteSession}
                        onMove={handleMoveSession}
                      />
                    ))}
                  </SessionList>
                ) : (
                  <UnfiledDropHint>Drop chats here</UnfiledDropHint>
                )}
              </SessionDropZone>
            )}
          </>
        )}
      </ScrollArea>
      <SidebarAccountButton
        gatewayLabel={state.agentUrl ?? config.gatewayUrl ?? ''}
        onLogout={config.onLogout}
        logoutLabel={config.logoutLabel}
      />
    </Container>
  );
}

// --- styling ---

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
  color: var(--text-primary);
  font-size: 12px;
  /* Scroll the whole sidebar as one — the Header (New Chat, New
     Folder, Examples, Tasks…) and SearchBar should slide out of view
     when the user pans up from anywhere, not just the chats list. */
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  ${THIN_SCROLLBAR}
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 2px;
  padding: 8px 8px 8px;
  flex-shrink: 0;
  border-bottom: 1px solid var(--divider-color);
`;

// Compact-mode collapse control, at the very top of the sidebar (right-aligned)
// so it reads as the sidebar's own header once the package top bar is hidden.
const CompactHeaderRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 2px;
`;

const CollapseSidebarButton = styled.button`
  all: unset;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 5px;
  color: var(--text-label);

  &:hover {
    color: var(--text-primary);
    background: var(--hover-bg);
  }
`;

const PrimaryButton = styled.button<{ $active?: boolean }>`
  width: 100%;
  height: 30px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 12px 0 8px;
  border-radius: 6px;
  border: none;
  background: ${(p) =>
    p.$active === true ? 'var(--hover-bg)' : 'transparent'};
  color: var(--text-primary);
  font-size: 12px;
  font-weight: 400;
  text-align: left;
  font-family: inherit;
  cursor: pointer;

  &:hover {
    background: var(--hover-bg);
  }
`;

// Uniform leading-icon slot so the top-of-sidebar buttons (New Chat, New
// Folder, Apps & Skills, …) align their text labels to the same x position
// regardless of which glyph they use.
const ButtonIconSlot = styled.span`
  width: 14px;
  height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const SearchBar = styled.div`
  flex-shrink: 0;
  padding: 8px;
  border-bottom: 1px solid var(--divider-color);
`;

const SearchInput = styled.input`
  width: 100%;
  height: 32px;
  padding: 0 8px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--input-bg-color);
  color: var(--text-primary);
  font-family: inherit;
  font-size: 12px;
  font-weight: 400;

  &::placeholder {
    color: var(--text-secondary);
    opacity: 0.7;
  }

  &:focus {
    outline: none;
    border-color: var(--text-secondary);
  }
`;

const ScrollArea = styled.div`
  padding: 8px 8px 12px;
  /* Container is the scroll parent now; let this section size to
     its content and overflow upward, where Container scrolls it. */
  flex-shrink: 0;
`;

// Horizontal rule placed between the folders area and the unfiled
// chats section so the visual break is obvious.
const SectionDivider = styled.div`
  height: 1px;
  background: var(--divider-color);
  margin: 8px 12px;
`;

const EmptyHint = styled.div`
  padding: 24px;
  text-align: center;
  color: var(--text-secondary);
  font-size: 12px;
  line-height: 1.5;
`;

// Placeholder shown when the unfiled drop zone is empty. Needs enough height
// to be an easy drop target (a tiny strip is hard to hit mid-drag), and the
// hint copy makes the affordance obvious — without it the user can't tell
// that the empty area accepts drops.
const UnfiledDropHint = styled.div`
  min-height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  color: var(--text-label);
  font-size: 11px;
  text-align: center;
  opacity: 0.7;
`;

const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

/**
 * The qualifier under a "no matches" result.
 *
 * Smaller and quieter than the result itself: it is a caveat on the answer,
 * not a second answer, and the action it names (open the chat) is one the
 * user can take from the list right above it.
 */
const UnsearchableNote = styled.span`
  display: block;
  margin-top: 6px;
  font-size: 11px;
  line-height: 1.4;
  color: var(--text-secondary);
  opacity: 0.85;
`;

const SearchingShimmer = styled.span`
  background: linear-gradient(
    90deg,
    var(--text-secondary) 0%,
    var(--text-secondary) 35%,
    var(--text-primary) 50%,
    var(--text-secondary) 65%,
    var(--text-secondary) 100%
  );
  background-size: 200% 100%;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  animation: ${shimmer} 2.5s linear infinite;
`;
